import { error, warn } from '@ali/re-utils';
import { splitKey } from '../utils';
import Types from '../types';

export default class MList {
  constructor(list, owner) {
    this.rawData = list || [];
    this.owner = owner;
    // fix: set hash to undefined, to emit a change when set from [{}] to []
    this.hash = undefined;
  }

  init() {
    if (this.inited) return;
    this.inited = true;
    this.items = this.rawData.map(data => this.owner.create(data));
    this.rawData = null;
  }

  setData(data, merge, targetKeys) {
    this.init();
    const releaseChange = this.owner.changeCapture();
    const origData = this.items.slice();
    let changed = false;
    this.items = data.map((raw, index) => {
      if (this.has(index)) {
        const orig = origData[index];
        if (orig.canReceive(raw)) {
          if (orig.setData(raw, merge)) {
            changed = true;
          }
          return orig;
        }

        orig.purge();
      } else {
        changed = true;
      }

      return this.owner.create(raw);
    });

    if (data.length < origData.length) {
      // length should be a property, so default merge length
      if (merge === 2) {
        this.items = this.items.concat(origData.slice(data.length));
        changed = true;
      }
    }
    const hash = this.items.map(item => item.id).join();
    if (hash !== this.hash) {
      changed = true;
    }
    this.hash = hash;
    if (changed) {
      this.owner.markChange();
      releaseChange(targetKeys, false);
    }
    return changed;
  }

  clear(force = true) {
    this.items = [];
    this.rawData = [];
    if (force) {
      this.hash = '';
    }
    this.owner.statChange();
  }

  into(data, type, merge, targetKeys) {
    if (!type) {
      type = Types.test(data);
    }

    switch (type) {
      case Types.LIST:
        return this.setData(data, merge, targetKeys);
      case Types.NULL:
        if (!merge) {
          this.clear();
        }
        break;
      default:
        error(`List cannot receive data of type "${type}"`, data);
    }
    return true;
  }

  toJS(force) {
    this.init();
    return this.items.map(item => item.toJS(force));
  }

  valueOf() {
    return this.toJS();
  }

  toString() {
    return this.valueOf().toString();
  }

  size() {
    this.init();
    return this.items.length;
  }

  removeItem(item) {
    this.init();
    const i = this.items.indexOf(item);
    if (i > -1) {
      item.purge();
      this.items.splice(i, 1);
      // 修正hash
      this.hash = this.items.map(k => k.id).join();
      this.owner.statChange([`${i}`]);
    }
  }

  keyOf(item) {
    this.init();
    const i = this.items.indexOf(item);
    if (i < 0) return null;
    return String(i);
  }

  sort(fn) {
    this.init();
    return this.items.sort(fn);
  }

  filter(fn) {
    this.init();
    return this.items.filter(fn);
  }

  findIndex(fn) {
    this.init();
    for (let i = 0, l = this.items.length; i < l; i++) {
      if (fn(this.items[i], i, this.items)) {
        return i;
      }
    }
    return -1;
  }

  find(fn) {
    this.init();
    const i = this.findIndex(fn);
    return i < 0 ? null : this.items[i];
  }

  every(fn) {
    this.init();
    return this.items.every(fn);
  }

  some(fn) {
    this.init();
    return this.items.some(fn);
  }

  map(fn) {
    this.init();
    return this.items.map(fn);
  }

  forEach(fn) {
    this.init();
    this.items.forEach(fn);
  }

  splice(start, deleteCount, ...items) {
    this.init();
    const newItems = items.map(data => this.owner.create(data));
    const removeItems = this.items.splice(start, deleteCount, ...newItems);
    if (items.length > 0 || removeItems.length > 0) {
      removeItems.forEach(item => item.purge());
      this.hash = this.items.map(k => k.id).join();
      this.owner.statChange([this.owner.getSelfKey()]);
    }
    return removeItems;
  }

  slice(start, end) {
    this.init();
    return this.items.slice(start, end);
  }

  shift() {
    this.init();
    const item = this.items.shift();
    if (item) {
      item.purge();
      this.owner.statChange();
    }
    return item;
  }

  pop() {
    this.init();
    const item = this.items.pop();
    if (item) {
      item.purge();
      this.owner.statChange();
    }
    return item;
  }

  has(key) {
    this.init();
    return key >= 0 && key < this.size();
  }

  set(key, data, merge) {
    this.init();
    const releaseChange = this.owner.changeCapture();
    if (this.has(key)) {
      const type = Types.test(data);
      const orig = this.items[key];
      if (type === Types.MUTABLE) {
        if (orig !== data) {
          orig.purge();
          this.items[key] = data;
          data.setOwner(this.owner);
        }
      } else if (!orig.canReceiveType(type)) {
        orig.purge();
        this.items[key] = this.owner.create(data);
      } else {
        orig.setData(data, merge);
      }
    } else {
      warn(`Not exists key "${key}"`);
    }
    this.owner.markChange();
    releaseChange([key], false);
  }

  get(key) {
    const keyPath = splitKey(key);
    if (!keyPath) {
      return null;
    }

    if (keyPath[1]) {
      const sup = this.owner.getOwner();
      return sup ? sup.get(keyPath[2]) : null;
    }

    this.init();

    let item = this.owner;
    const curKey = keyPath[3];
    const nestPath = keyPath[4];

    if (curKey) {
      item = this.has(curKey) ? this.items[curKey] : null;
    }

    if (!item || !nestPath) {
      return item;
    }

    return item.get(nestPath);
  }

  getValue(key) {
    const item = this.get(key);
    return item ? item.getValue() : null;
  }
}
